import { Component, EventEmitter, OnInit, ViewContainerRef } from '@angular/core';
import { AuthService } from '../../auth/services/auth.service';
import { CustomerImageType } from '../../customer/models/enums/customer-image-type.enum';
import { CustomerManageService } from '../../customer/shared/customer-manage.service';
import { OOJCredentialViewContext, OOJCredentialViewModalComponent } from '../../driver/credential/issuance/credential/ooj-credential-modal.component';
import { DriverCredentialStatusType } from '../../driver/models/enums/driver-credential-status-type.enum';
import { DriverImageStatusType } from '../../driver/models/enums/driver-image-status-type.enum';
import { DriverCredentialService } from '../../driver/services/driver-credential.service';
import { AccessControlType } from '../../foundation/models/enums/access-control-type.enum';
import { IJurisdiction, Jurisdiction } from '../../foundation/models/enums/jurisdiction.enum';
import { ProhibitionService } from '../../prohibition/services/prohibition.service';
import { BaseComponent } from '../../shared/base-components/base.component';
import { ModalSize } from '../../shared/services/modal/mvd-modal.enum';
import { MvdModalService } from '../../shared/services/modal/mvd-modal.service';
import { DrivingRecordCredentialModel } from '../models/driver-record-credential.model';
import { AddCredentialContext, AddCredentialModalComponent } from './add-credential-modal.component';

@Component({
    selector: 'driver-record-credentials',
    templateUrl: './driver-record-credentials.html',
    inputs: [
        'customerNumberId',
        'businessTransactionNumberId',
        'showActions',
        'showAddCredential',
        'showInStateCredentialsOnly',
        'selectableRows',
        'selectedCredentialIds'
    ],
    outputs: ['actionChange']
})
export class DriverRecordCredentialsComponent extends BaseComponent implements OnInit {
    public drivingRecordCredentials: DrivingRecordCredentialModel[] = [];
    public filteredDrivingRecordEvents: DrivingRecordCredentialModel[] = [];
    public showAllHistory = false;
    public twelveMonthPoints = 0;
    public thirtySixMonthPoints = 0;
    public customerNumberId = 0;
    public showActions = false;
    public showAddCredential = true;
    public showInStateCredentialsOnly = false;
    public selectableRows = false;
    public businessTransactionNumberId: number;
    public statusTypes = new DriverCredentialStatusType();
    public actionChange = new EventEmitter();
    public jurisdictionEnum = new Jurisdiction();
    public selectedCredentials: DrivingRecordCredentialModel[] = [];
    public expandedRows: any = {};
    public driverImageStatusType = new DriverImageStatusType();
    public selectedCredentialIds: number[];
    public isRestrictedCustomer = false;
    public isUserLecu = false;
    public hasOOJCredential = false;
    public canEditOOJCredential = false;
    public canDeleteOOJCredential = false;
    private baseJurisdiction: IJurisdiction;

    constructor(
        public viewContainer: ViewContainerRef,
        public modalService: MvdModalService,
        private prohibitionService: ProhibitionService,
        private customerService: CustomerManageService,
        private credentialService: DriverCredentialService,
        authService: AuthService
    ) {
        super();

        const accessControls = new AccessControlType();
        this.canEditOOJCredential = authService.hasACSTransactionClaimEx(authService.acsTransactionClaim, accessControls.EditOOJCredential.value);
        this.canDeleteOOJCredential = authService.hasACSTransactionClaimEx(authService.acsTransactionClaim, accessControls.DeleteOOJCredential.value);
        this.baseJurisdiction = this.getBaseJurisdiction();
    }

    public ngOnInit() {
        if (this.customerNumberId) {
            this.isUserLecu = this.getIsUserLecu();
            if (!this.isUserLecu) {
                // Get is customer restricted based on this will redact the data
                this.getIsCustomerRestricted(this.customerNumberId);
            }
        }
    }

    public ngOnChanges() {
        if (this.customerNumberId > 0) {
            this.loadCredentials();
        }
    }

    /**
     * Checks to see whether the user is acccessing the system as part of the LECU business unit.
     * @returns a boolean indicating whether the user is accessing the system as part of the LECU business unit.
     * */
    private getIsUserLecu(): boolean {
        const businessUnit = localStorage.getItem('mvd:bu');

        return businessUnit.indexOf('LECU') > 0;
    }

    /**
     * Fetching customer restricted details to redact the fields based on flag
     * @param customerNumber
     */
    private getIsCustomerRestricted(customerNumber: number) {
        if (customerNumber && customerNumber != null) {
            this.customerService.isCustomerRestricted(customerNumber).subscribe(
                r => this.isRestrictedCustomer = r.result,
                e => this.handleValidationResult(e)
            );
        }
    }

    /**
     * Load events into table.
     */
    public loadCredentials = (): void => {
        this.prohibitionService.getDrivingRecordCredentials(this.customerNumberId, this.businessTransactionNumberId)
            .subscribe(
                c => {
                    this.drivingRecordCredentials = c.result;
                    if ((null != this.selectedCredentialIds) && (null != this.selectedCredentials) && (0 == +this.selectedCredentials.length)) {
                        this.selectedCredentials = this.drivingRecordCredentials.filter(e => this.selectedCredentialIds.includes(e.driverImageId));
                    }

                    this.drivingRecordCredentials.forEach(credential => {
                        credential.imageType = this.isBaseJurisdiction(credential.jurisdictionDescription) ? CustomerImageType.CREDENTIAL : CustomerImageType.OUT_OF_JURISDICTION;
                    });

                    if (this.showInStateCredentialsOnly) {
                        this.drivingRecordCredentials = this.drivingRecordCredentials.filter(c => c.imageType == CustomerImageType.CREDENTIAL);
                    }

                    if (this.selectableRows) {
                        this.drivingRecordCredentials.forEach(c => this.expandedRows[c.id] = true);
                    }

                    this.hasOOJCredential = this.drivingRecordCredentials.some(c => !this.isBaseJurisdiction(c.jurisdictionDescription));
                },
                e => console.log('error', e)
            );
    };

    public onActionChange = (event, id): void => {
        this.actionChange.emit({ event, id});
    };

    public addCredential = (): void => {
        let data = new AddCredentialContext(this.businessTransactionNumberId, 0, this.customerNumberId);
        this.modalService
            .open(AddCredentialModalComponent, data, ModalSize.Medium)
            .afterClosed()
            .subscribe(() => this.loadCredentials());
    };

    public editOOJCredential = (oojCredentialId: number): void => {
        let data = new OOJCredentialViewContext(0, oojCredentialId, false, this.customerNumberId);
        this.modalService
            .open(OOJCredentialViewModalComponent, data, ModalSize.Medium)
            .afterClosed()
            .subscribe(() => this.loadCredentials());
    };

    public deleteOOJCredential = (oojCredentialId: number): void => {
        this.modalService
            .confirm()
            .body('Are you sure you want to delete this credential? This action can not be undone.')
            .okBtn('Yes')
            .cancelBtn('No')
            .open()
            .afterClosed()
            .subscribe(result => {
                if (!!result) {
                    this.credentialService.deactivateOOJCredential(oojCredentialId).subscribe(
                        _ => this.loadCredentials(),
                        e => this.handleValidationResult(e),
                    );
                }
            });
    };

    /**
     * Checks whether the given jurisdiction description matches the base jurisdiction code.
     *
     * @param jurisdictionDescription The jurisdiction to check.
     * @returns True if the jurisdiction is the base jurisdiction.
     */
    public isBaseJurisdiction(jurisdictionDescription: string): boolean {
        return jurisdictionDescription == this.baseJurisdiction.code;
    }
}
